import React, { useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Grid2,
  Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { backArrow, calenderIcon, info } from "../../assets/Icons";
import GenericMetricsCards from "../../components/GenericMetricsCards/GenericMetricsCards";
import { useDispatch, useSelector } from "react-redux";
import { getPatientPopulationVitals } from "../../redux/reducers/patientReducer";
import Loader from "../../components/Loader";
import withNavigate from "../../hocs/withNavigate";

// default range
const ranges = {
  BloodGlucose: {
      critical: [[0, 69], [181, Infinity]],
      high: [[161, 180]],
      borderline: [[70, 89], [141, 160]],
      normal: [[90, 140]],
  },
  HeartRate: {
      critical: [[0, 39], [131, Infinity]],
      high: [[40, 49], [110, 130]],
      borderline: [[50, 59], [101, 109]],
      normal: [[60, 100]],
  },
  SPO2: {
      critical: [[0, 84]],
      high: [[85, 89]],
      borderline: [[90, 95]],
      normal: [[96, 100]],
  },
  SBP: {
      critical: [[0, 79], [181, Infinity]],
      high: [[80, 85], [140, 180]],
      borderline: [[86, 90], [130, 139]],
      normal: [[91, 129]],
  },
  DBP: {
      critical: [[0, 49], [116, Infinity]],
      high: [[50, 55], [101, 115]],
      borderline: [[56, 60], [91, 100]],
      normal: [[61, 90]],
  },
  RespirationRate: {
      critical: [[0, 9], [31, Infinity]],
      high: [[10, 11], [21, 30]],
      borderline: [], // No yellow zone in image
      normal: [[12, 20]],
  },
};

// Dummy patient data for different health metrics
const patientData = [
  {
    name: "Blood Glucose (mg/dL)",
    info: true,
    infoText: ranges.BloodGlucose,
    code: 'BloodGlucose',
    metrics: [
      { label: "Normal", code: "normal", count: 6, bgColor: "#5CBE85", color: "#169459" },
      { label: "Borderline", code: "borderline", count: 1, bgColor: "#FEF3C7", color: "#CAC65C" },
      { label: "High", code: "high", count: 1, bgColor: "#F6A344", color: "#D7832F" },
      { label: "Critical", code: "critical", count: 0, bgColor: "#F16A68", color: "#B4343C" },
    ],
  },
  {
    name: "Systolic Blood Pressure (mmHg)",
    info: true,
    infoText: ranges.SBP,
    code: 'SBP',
    metrics: [
      { label: "Normal", code: "normal", count: 6, bgColor: "#5CBE85", color: "#169459" },
      { label: "Borderline", code: "borderline", count: 1, bgColor: "#FEF3C7", color: "#CAC65C" },
      { label: "High", code: "high", count: 1, bgColor: "#F6A344", color: "#D7832F" },
      { label: "Critical", code: "critical", count: 0, bgColor: "#F16A68", color: "#B4343C" },
    ],
  },
  {
    name: "Diastolic Blood Pressure (mmHg)",
    info: true,
    infoText: ranges.DBP,
    code: 'DBP',
    metrics: [
      { label: "Normal", code: "normal", count: 6, bgColor: "#5CBE85", color: "#169459" },
      { label: "Borderline", code: "borderline", count: 1, bgColor: "#FEF3C7", color: "#CAC65C" },
      { label: "High", code: "high", count: 1, bgColor: "#F6A344", color: "#D7832F" },
      { label: "Critical", code: "critical", count: 0, bgColor: "#F16A68", color: "#B4343C" },
    ],
  },
  {
    name: "Heart Rate (bpm)",
    info: true,
    infoText: ranges.HeartRate,
    code: 'HeartRate',
    metrics: [
      { label: "Normal", code: "normal", count: 6, bgColor: "#5CBE85", color: "#169459" },
      { label: "Borderline", code: "borderline", count: 1, bgColor: "#FEF3C7", color: "#CAC65C" },
      { label: "High", code: "high", count: 1, bgColor: "#F6A344", color: "#D7832F" },
      { label: "Critical", code: "critical", count: 0, bgColor: "#F16A68", color: "#B4343C" },
    ],
  },
  {
    name: "Oxygen Saturation (Percentage)",
    info: true,
    infoText: ranges.SPO2,
    code: 'SPO2',
    metrics: [
      { label: "Normal", code: "normal", count: 6, bgColor: "#5CBE85", color: "#169459" },
      { label: "Borderline", code: "borderline", count: 1, bgColor: "#FEF3C7", color: "#CAC65C" },
      { label: "High", code: "high", count: 1, bgColor: "#F6A344", color: "#D7832F" },
      { label: "Critical", code: "critical", count: 0, bgColor: "#F16A68", color: "#B4343C" },
    ],
  },
  {
    name: "Respiration Rate (Percentage)",
    info: true,
    infoText: ranges.RespirationRate,
    code: 'RespirationRate',
    metrics: [
      { label: "Normal", code: "normal", count: 6, bgColor: "#5CBE85", color: "#169459" },
      { label: "High", code: "high", count: 1, bgColor: "#F6A344", color: "#D7832F" },
      { label: "Critical", code: "critical", count: 0, bgColor: "#F16A68", color: "#B4343C" },
    ],
  },
];

const PatientPopulation = ({ navigate }) => {
  const dispatch = useDispatch();
  const { patientPopulationsVitals = {}, loading } = useSelector((state) => state?.patient);

  const updatedPatientData = patientData.map((category) => {
    const vitalData = patientPopulationsVitals[category.code] || {};
    const updatedMetrics = category.metrics.map((metric) => ({
      ...metric,
      count: vitalData[metric.code]?.length || 0,
    }));
    return { ...category, metrics: updatedMetrics };
  });

  useEffect(() => {
    dispatch(getPatientPopulationVitals());
  }, [dispatch]);

  return (
    <Box>
      {/* Header Section */}
      <Grid2
        container
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: { xs: "wrap", md: "nowrap" }, p: { xs: 1, sm: 2 } }}
      >
        {/* Header Section */}
        <Grid2
          item
          xs={12}
          sm={"auto"}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
            justifyContent: { xs: "flex-start", sm: "flex-start" },
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.8rem" },
              whiteSpace: "nowrap",
            }}
          >
            Patient Population
          </Typography>
        </Grid2>

        {/* Action Buttons & Search Field */}
        <Grid2
          item
          xs={12}
          sm
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: { xs: 1, sm: 1.5 },
            mt: { xs: 2, sm: 0 },
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/view-all-patient')}
            sx={{
              fontSize: "0.75rem",
              bgcolor: "#fff",
              color: "#000",
              border: "none",
              borderRadius: "50px",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
              fontWeight: "500",
              minWidth: "110px",
              height: "38px",
            }}
          >
            View All Patients
          </Button>
        </Grid2>
      </Grid2>

      {/* Data Sections */}
      <Box sx={{ bgcolor: "#F5F5F5", boxShadow: 2 }}>
        {updatedPatientData.map((category) => (
          <Box key={category.name}>
            {/* Category Header */}
            <Box sx={{ padding: 1, display: "flex", alignItems: "center" }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ paddingLeft: 2 }}
              >
                {category.name}
              </Typography>
              {category.info && (
                <Tooltip
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: '0.9rem',
                        backgroundColor: '#fff',
                        color: '#000',
                        boxShadow: 2,
                        border: '1px solid #ccc',
                        whiteSpace: 'pre-line' // ensures line breaks work
                      },
                    },
                  }}
                  title={
                    <div>
                      {Object.entries(category.infoText).map(([level, ranges]) => (
                        <div key={level}>
                          <strong>{level}</strong>:{" "}
                          {ranges.map(([min, max], idx) => (
                            <span key={idx}>
                              ({min} - {max === Infinity ? '∞' : max})
                              {idx < ranges.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <IconButton sx={{ color: "black", ml: 1 }}>
                    <img src={info} alt="Info" width={30} height={30} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* GenericMetricsCards Grid2 */}
            <GenericMetricsCards metrics={category} />
          </Box>
        ))}
      </Box>
      <Loader loading={loading} />
    </Box>
  );
};

export default withNavigate(PatientPopulation);
